import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { PhPaperclip, PhListChecks, PhCheck } from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';
import SectionPanel from 'tio-ui/components/section-panel';
import { getFieldValue, getFieldValues } from '../../../utils/tas/program-builder/sections';
import type IntlService from 'ember-intl/services/intl';
import type { ProgramTypeMeta } from '../../../utils/tas/program-builder/sections';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

type FieldsSignature = TASProgramTemplateModel['fields'];

// Same staged attachment groups as the builder's Documents section.
const DOC_GROUPS = [
  {
    key: 'program',
    titleKey: 'program_builder.documents.group_program_title',
    stringField: 'PROGRAM_APPROVAL_DOCUMENTS_STRING',
    listField: 'PROGRAM_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
  },
  {
    key: 'course',
    titleKey: 'program_builder.documents.group_course_title',
    stringField: 'COURSE_APPROVAL_DOCUMENTS_STRING',
    listField: 'COURSE_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
  },
  {
    key: 'completion',
    titleKey: 'program_builder.documents.group_completion_title',
    stringField: 'COMPLETION_DOCUMENTS_STRING',
    listField: 'COMPLETION_DOCUMENTS_ATTACHMENT_FIELD',
  },
];

const DOC_TYPE_LABEL_KEYS: Record<string, string> = {
  general: 'program_builder.documents.doc_type_general_label',
  transcript: 'program_builder.documents.doc_type_transcript_label',
  grade: 'program_builder.documents.doc_type_grade_label',
  receipt: 'program_builder.documents.doc_type_receipt_label',
  certificate: 'program_builder.documents.doc_type_certificate_label',
};

interface ReadoutDocumentsPanelSignature {
  Args: {
    fields: FieldsSignature;
    typeMeta: ProgramTypeMeta;
    /** Reveal internal FIELD_NAMES badges (admin builder's devops toggle). */
    showFieldNames?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * Read-only Documents & Evidence: each staged attachment group with its
 * visibility/required flags, instructions, and the actual required documents
 * (name + categorized type), plus the evidence-waiver checklists.
 */
export default class ReadoutDocumentsPanel extends Component<ReadoutDocumentsPanelSignature> {
  @service declare intl: IntlService;

  private bool(name: string): boolean {
    return Boolean(getFieldValue(this.args.fields, name));
  }

  private strings(name: string): string[] {
    return (getFieldValues(this.args.fields, name) as string[]).filter(
      (value) => typeof value === 'string' && value.trim()
    );
  }

  /** Canonical attachment shape: `values` = label strings, `value_document_types`
   * = label → type map ('general' omitted). Tolerates stray legacy entries the
   * same way the builder does; blank labels are skipped (runtime ignores them). */
  private docs(listField: string) {
    const config = this.args.fields as unknown as Record<
      string,
      { value_document_types?: Record<string, string> } | undefined
    >;
    const map = config[listField]?.value_document_types ?? {};
    const raw = getFieldValues(this.args.fields, listField) as unknown[];
    const rows: { label: string; typeLabel: string }[] = [];
    for (const entry of raw) {
      if (Array.isArray(entry)) continue;
      let label = '';
      let type: string | undefined;
      if (typeof entry === 'string') {
        label = entry;
      } else if (entry && typeof entry === 'object') {
        const obj = entry as { label?: string; documentType?: string };
        label = typeof obj.label === 'string' ? obj.label : '';
        type = obj.documentType;
      }
      if (!label.trim()) continue;
      const key = type ?? map[label] ?? 'general';
      const labelKey = DOC_TYPE_LABEL_KEYS[key];
      rows.push({ label, typeLabel: labelKey ? this.intl.t(labelKey) : key });
    }
    return rows;
  }

  /** Tracking-only programs only stage program-approval documents. */
  get groups() {
    const defs = this.args.typeMeta.trackingOnly
      ? DOC_GROUPS.filter((group) => group.key === 'program')
      : DOC_GROUPS;
    const config = this.args.fields as unknown as Record<
      string,
      { visible?: boolean; required?: boolean } | undefined
    >;
    return defs.map((def) => {
      const stringConfig = config[def.stringField] ?? {};
      return {
        title: this.intl.t(def.titleKey),
        stringField: def.stringField,
        listField: def.listField,
        instructions: String(getFieldValue(this.args.fields, def.stringField) ?? '').trim(),
        visible: Boolean(stringConfig.visible),
        required: Boolean(stringConfig.required),
        docs: this.docs(def.listField),
      };
    });
  }

  get showEvidenceWaivers() {
    return !this.args.typeMeta.trackingOnly;
  }

  get allowWaiver() {
    return this.bool('ALLOW_WAIVER_OF_EVIDENCE_APPROVAL');
  }

  get approvalWaivers() {
    return this.strings('EVIDENCE_APPROVAL_WAIVER_CHECKLIST');
  }

  get attachmentWaivers() {
    return this.strings('EVIDENCE_ATTACHMENT_WAIVER_CHECKLIST');
  }

  <template>
    <div class="space-y-5" ...attributes>
      {{#each this.groups as |group|}}
        <SectionPanel @icon={{PhPaperclip}} @title={{group.title}} @iconColor="violet">
          <:trailing>
            <div class="tio-row-loose text-xs tio-fg-muted">
              <span class="tio-row-extra-tight">
                {{t "program_builder.documents.visible"}}
                {{#if group.visible}}
                  <PhCheck @size={{14}} class="tio-fg-success" aria-hidden="true" />
                {{else}}
                  <span class="tio-fg-faint">—</span>
                {{/if}}
              </span>
              <span class="tio-row-extra-tight">
                {{t "program_builder.documents.required"}}
                {{#if group.required}}
                  <PhCheck @size={{14}} class="tio-fg-success" aria-hidden="true" />
                {{else}}
                  <span class="tio-fg-faint">—</span>
                {{/if}}
              </span>
            </div>
          </:trailing>
          <:body>
            <div class="tio-text-overline tio-fg-muted">
              {{t "program_builder.readout.instructions_label"}}
              {{#if @showFieldNames}}
                <code
                  class="ml-1 font-mono normal-case tio-text-micro tio-fg-brand"
                >{{group.stringField}}</code>
              {{/if}}
            </div>
            <p class="mb-0 mt-1 text-sm {{unless group.instructions 'tio-fg-faint'}}">
              {{if group.instructions group.instructions "—"}}
            </p>

            <div class="tio-text-overline mt-3 tio-fg-muted">
              {{t "program_builder.documents.required_documents_heading"}}
              {{#if @showFieldNames}}
                <code
                  class="ml-1 font-mono normal-case tio-text-micro tio-fg-brand"
                >{{group.listField}}</code>
              {{/if}}
            </div>
            {{#if group.docs.length}}
              <div class="mt-1.5 space-y-1.5">
                {{#each group.docs as |doc|}}
                  <div
                    class="tio-row rounded-lg border tio-border-default px-3 py-2"
                    data-test-readout-document={{doc.label}}
                  >
                    <span class="tio-fill-min text-sm">{{doc.label}}</span>
                    <Chip @size="sm" @intent="default" @appearance="faded">{{doc.typeLabel}}</Chip>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <p class="mb-0 mt-1 text-sm tio-fg-faint">—</p>
            {{/if}}
          </:body>
        </SectionPanel>
      {{/each}}

      {{#if this.showEvidenceWaivers}}
        <SectionPanel
          @icon={{PhListChecks}}
          @title={{t "program_builder.documents.evidence_waivers"}}
          @iconColor="violet"
        >
          <:body>
            <div class="tio-row-between gap-3 text-sm">
              <span class="tio-fg-muted">{{t
                  "program_builder.readout.allow_evidence_waiver"
                }}</span>
              {{#if this.allowWaiver}}
                <PhCheck @size={{16}} class="tio-fg-success" aria-hidden="true" />
              {{else}}
                <span class="tio-fg-faint">—</span>
              {{/if}}
            </div>
            {{#if this.allowWaiver}}
              <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <div class="tio-text-overline mb-1.5 tio-fg-muted">
                    {{t "program_builder.readout.approval_waivers"}}
                  </div>
                  <ul class="m-0 list-disc space-y-1 pl-5 text-sm">
                    {{#each this.approvalWaivers as |waiver|}}
                      <li>{{waiver}}</li>
                    {{else}}
                      <li class="list-none -ml-5 tio-fg-faint">—</li>
                    {{/each}}
                  </ul>
                </div>
                <div>
                  <div class="tio-text-overline mb-1.5 tio-fg-muted">
                    {{t "program_builder.readout.attachment_waivers"}}
                  </div>
                  <ul class="m-0 list-disc space-y-1 pl-5 text-sm">
                    {{#each this.attachmentWaivers as |waiver|}}
                      <li>{{waiver}}</li>
                    {{else}}
                      <li class="list-none -ml-5 tio-fg-faint">—</li>
                    {{/each}}
                  </ul>
                </div>
              </div>
            {{/if}}
          </:body>
        </SectionPanel>
      {{/if}}
    </div>
  </template>
}
